/**
 * O Markdown dos termos e da política (legal/*.md), só o que eles usam: títulos (`#`, `##`, `###`),
 * parágrafos, listas com `- ` (a continuação vem recuada em 2 espaços), tabelas com `|`, `**negrito**`
 * e `[links](endereço)`. Vira uma árvore simples que o React desenha: nada de HTML no texto.
 */

export type Inline = { kind: 'text'; text: string } | { kind: 'strong'; children: Inline[] } | { kind: 'link'; href: string; children: Inline[] };

export type Block =
  | { kind: 'heading'; level: 1 | 2 | 3; text: string; id: string }
  | { kind: 'paragraph'; inlines: Inline[] }
  | { kind: 'list'; items: Inline[][] }
  | { kind: 'table'; head: Inline[][]; rows: Inline[][][] };

/** O id de um título, para o sumário: "8. Os seus direitos" → "8-os-seus-direitos". */
export function slug(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

const INLINE = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)\s]+)\)/g;

export function parseInline(text: string): Inline[] {
  const out: Inline[] = [];
  let last = 0;
  for (const match of text.matchAll(INLINE)) {
    if (match.index > last) out.push({ kind: 'text', text: text.slice(last, match.index) });
    if (match[1] !== undefined) out.push({ kind: 'strong', children: parseInline(match[1]) });
    else out.push({ kind: 'link', href: match[3]!, children: parseInline(match[2]!) });
    last = match.index + match[0].length;
  }
  if (last < text.length) out.push({ kind: 'text', text: text.slice(last) });
  return out;
}

const cells = (line: string): string[] =>
  line
    .trim()
    .replace(/^\||\|$/g, '')
    .split('|')
    .map((cell) => cell.trim());

export function parseMarkdown(source: string): Block[] {
  const blocks: Block[] = [];
  const lines = source.replace(/\r\n?/g, '\n').split('\n');
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (!line.trim()) {
      i++;
      continue;
    }
    const heading = /^(#{1,3}) (.+)$/.exec(line);
    if (heading) {
      const text = heading[2]!.trim();
      blocks.push({ kind: 'heading', level: heading[1]!.length as 1 | 2 | 3, text, id: slug(text) });
      i++;
      continue;
    }
    if (line.startsWith('|')) {
      const head = cells(line).map(parseInline);
      i += 2; // o cabeçalho e a linha |---|
      const rows: Inline[][][] = [];
      while (i < lines.length && lines[i]!.startsWith('|')) rows.push(cells(lines[i++]!).map(parseInline));
      blocks.push({ kind: 'table', head, rows });
      continue;
    }
    if (line.startsWith('- ')) {
      const items: string[] = [];
      while (i < lines.length) {
        const current = lines[i]!;
        if (current.startsWith('- ')) items.push(current.slice(2).trim());
        else if (current.startsWith('  ') && current.trim() && items.length) items[items.length - 1] += ` ${current.trim()}`;
        else break;
        i++;
      }
      blocks.push({ kind: 'list', items: items.map(parseInline) });
      continue;
    }
    const text: string[] = [];
    while (i < lines.length && lines[i]!.trim() && !/^(#{1,3} |- |\|)/.test(lines[i]!)) text.push(lines[i++]!.trim());
    blocks.push({ kind: 'paragraph', inlines: parseInline(text.join(' ')) });
  }
  return blocks;
}
